import { Layout, message, Drawer } from 'antd';
import IconSideMenuSwitcher from 'assets/Icon/IconSideMenuSwitcher';
import DynamicBreadcrumb from 'components/Breadcrumb';
import { PATH_UNUSED_BREADCRUMB } from 'constants/routers';
import { CookieKeys } from 'constants/services';
import React, { ReactNode, memo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { getCookie } from 'utils/cookies';
import TCommonHeader from './Header';
import TCommonSidebar from './Sidebar';

const { Content, Header, Sider } = Layout;
const TCommonLayout: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(true);
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Hotkey: Alt + R or Ctrl + Shift + R
      if ((e.altKey && e.key.toLowerCase() === 'r') || (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'r')) {
        e.preventDefault();
        navigate('/auto-register');
        message.success('Chuyển nhanh sang trang Đăng ký tự động!');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  useEffect(() => {
    const handleToggleMobileSidebar = () => {
      setMobileSidebarOpen(prev => !prev);
    };
    const handleCloseMobileSidebar = () => {
      setMobileSidebarOpen(false);
    };
    window.addEventListener('toggle-mobile-sidebar', handleToggleMobileSidebar);
    window.addEventListener('close-mobile-sidebar', handleCloseMobileSidebar);
    return () => {
      window.removeEventListener('toggle-mobile-sidebar', handleToggleMobileSidebar);
      window.removeEventListener('close-mobile-sidebar', handleCloseMobileSidebar);
    };
  }, []);

  const isAuthenticated = () => {
    return !!getCookie(CookieKeys.Token);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024); // Use 1024px threshold (desktop standard breakpoint) to hide sidebar on tablet & mobile
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Tự động đóng Drawer khi thay đổi route
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Ẩn hoàn toàn Sidebar Sider nếu là màn hình nhỏ (isMobile) */}
      {!isMobile && (
        <Sider className="relative" trigger={null} collapsible collapsed={collapsed} width={250}>
          <TCommonSidebar />
          <div className="sider__switch absolute" onClick={() => setCollapsed(!collapsed)}>
            <IconSideMenuSwitcher />
          </div>
          {!collapsed && (
            <div className="absolute right-0 left-0 bottom-0 flex items-center h-14 text-xs justify-center text-black border-t border-gray border-opacity-60">
              <span>{t('common.label.copyright')}</span>
            </div>
          )}
        </Sider>
      )}

      <Layout>
        <Header style={{ padding: isMobile ? '0 15px' : '0 10px' }} className="bg-primary">
          <TCommonHeader isMobile={isMobile} />
        </Header>
        <Content
          style={{
            maxHeight: 'calc(100vh - 56px)',
            minHeight: 'calc(100vh - 56px)',
            overflowY: 'auto',
            padding: isMobile ? '12px' : 'initial'
          }}
        >
          {!PATH_UNUSED_BREADCRUMB.includes(location.pathname) && <DynamicBreadcrumb />}

          <div>{children}</div>
        </Content>
      </Layout>

      {/* Hiển thị Drawer thay thế cho Sider khi click Hamburger Menu trên màn hình nhỏ */}
      {isMobile && (
        <Drawer
          placement="left"
          onClose={() => setMobileSidebarOpen(false)}
          open={mobileSidebarOpen}
          width={250}
          styles={{
            body: { padding: 0, background: '#001529' },
            header: { background: '#001529', borderBottom: '1px solid #1f1f1f', color: '#fff' }
          }}
          title={<span style={{ color: '#fff', fontWeight: 600 }}>Menu Hệ Thống</span>}
        >
          <TCommonSidebar />
        </Drawer>
      )}
    </Layout>
  );
};

export default memo(TCommonLayout);
